import { normalizeFormDateStringToIso } from '~/utils/dateFormatter';

import { parseNonEmptyStrings } from '../parseHelpers';

import { createDefaultHeroCarouselItem, createDefaultOffer } from './defaults';
import { groupOffersByTag } from './offers';
import type { OffersHubHeroCarouselItem, OffersHubOffer, OffersHubPayloadBuilderState } from './types';

const PREVIEW_REQUIRED = '[required]';

function previewRequiredValue(value: string): string {
  return value.trim() ? value.trim() : PREVIEW_REQUIRED;
}

function previewDateTime(value: string): string {
  return value.trim() ? normalizeFormDateStringToIso(value) : PREVIEW_REQUIRED;
}

function buildHeroCarouselItem(item: OffersHubHeroCarouselItem): Record<string, unknown> {
  const logoImageId = item.logoImageId.trim();

  return {
    'label-i18n': item.label.trim(),
    imageId: item.imageId.trim(),
    ...(logoImageId ? { logoImageId } : {}),
    'title-i18n': item.title.trim(),
    'description-i18n': item.description.trim(),
    'ctaLabel-i18n': item.ctaLabel.trim(),
    ctaUrl: item.ctaUrl.trim(),
    startAt: normalizeFormDateStringToIso(item.startAt),
    endAt: normalizeFormDateStringToIso(item.endAt),
    isActive: item.isActive,
    enabledRegions: parseNonEmptyStrings(item.enabledRegions),
  };
}

function buildPreviewHeroCarouselItem(item: OffersHubHeroCarouselItem): Record<string, unknown> {
  const logoImageId = item.logoImageId.trim();

  return {
    'label-i18n': previewRequiredValue(item.label),
    imageId: previewRequiredValue(item.imageId),
    ...(logoImageId ? { logoImageId } : {}),
    'title-i18n': previewRequiredValue(item.title),
    'description-i18n': previewRequiredValue(item.description),
    'ctaLabel-i18n': previewRequiredValue(item.ctaLabel),
    ctaUrl: previewRequiredValue(item.ctaUrl),
    startAt: previewDateTime(item.startAt),
    endAt: previewDateTime(item.endAt),
    isActive: item.isActive,
    enabledRegions: parseNonEmptyStrings(item.enabledRegions),
  };
}

function buildOffer(offer: OffersHubOffer): Record<string, unknown> {
  const isPartnerOffer = offer.offerTag === 'partner_offer';

  return {
    offerTag: offer.offerTag,
    ...(isPartnerOffer ? { brandName: offer.brandName.trim() } : {}),
    ...(isPartnerOffer ? { industry: offer.industry.trim() } : {}),
    ...(isPartnerOffer ? { 'industry-i18n': offer.industry.trim() } : {}),
    imageId: offer.imageId.trim(),
    'title-i18n': offer.title.trim(),
    'description-i18n': offer.description.trim(),
    'ctaLabel-i18n': offer.ctaLabel.trim(),
    ctaUrl: offer.ctaUrl.trim(),
    urgencyType: offer.urgencyType,
    startAt: normalizeFormDateStringToIso(offer.startAt),
    endAt: normalizeFormDateStringToIso(offer.endAt),
    isActive: offer.isActive,
    enabledRegions: parseNonEmptyStrings(offer.enabledRegions),
  };
}

function buildPreviewOffer(offer: OffersHubOffer): Record<string, unknown> {
  const isPartnerOffer = offer.offerTag === 'partner_offer';

  return {
    offerTag: offer.offerTag,
    ...(isPartnerOffer ? { brandName: previewRequiredValue(offer.brandName) } : {}),
    ...(isPartnerOffer ? { industry: previewRequiredValue(offer.industry) } : {}),
    ...(isPartnerOffer ? { 'industry-i18n': previewRequiredValue(offer.industry) } : {}),
    imageId: previewRequiredValue(offer.imageId),
    'title-i18n': previewRequiredValue(offer.title),
    'description-i18n': previewRequiredValue(offer.description),
    'ctaLabel-i18n': previewRequiredValue(offer.ctaLabel),
    ctaUrl: previewRequiredValue(offer.ctaUrl),
    urgencyType: offer.urgencyType,
    startAt: previewDateTime(offer.startAt),
    endAt: previewDateTime(offer.endAt),
    isActive: offer.isActive,
    enabledRegions: parseNonEmptyStrings(offer.enabledRegions),
  };
}

export function buildOffersHubPayload(state: OffersHubPayloadBuilderState): Record<string, unknown> {
  return {
    heroCarouselItems: state.heroCarouselItems.map(buildHeroCarouselItem),
    offers: groupOffersByTag(state.offers).map(buildOffer),
  };
}

export function buildOffersHubPreviewPayload(state: OffersHubPayloadBuilderState): Record<string, unknown> {
  const heroCarouselItems =
    state.heroCarouselItems.length > 0 ? state.heroCarouselItems : [createDefaultHeroCarouselItem()];
  const offers = state.offers.length > 0 ? groupOffersByTag(state.offers) : [createDefaultOffer()];

  return {
    heroCarouselItems: heroCarouselItems.map(buildPreviewHeroCarouselItem),
    offers: offers.map(buildPreviewOffer),
  };
}

export function serializeOffersHubPayload(state: OffersHubPayloadBuilderState): string {
  return JSON.stringify(buildOffersHubPayload(state), null, 2);
}

export function serializeOffersHubPreviewPayload(state: OffersHubPayloadBuilderState): string {
  return JSON.stringify(buildOffersHubPreviewPayload(state), null, 2);
}

export function offersHubPayloadDownloadFilename(): string {
  return 'offers-hub-payload.txt';
}
